// AnalyticsFilters component
// UPDATED: Replaced two separate DatePicker components with a single DateRangePicker
// UPDATED: Aligned with Team page design patterns:
//   - Layout uses justify-between with left/right groups (matching Team page control bar)
//   - Site selector uses SiteSelector (BaseFilterDropdown, compact) matching Team page
//   - Mission selector uses BaseFilterDropdown (compact) matching Team page style
//   - Refresh button matches Team page styling (bg-white, border, rounded-lg)
//   - Site loading moved to useAnalyticsFilters hook
// UPDATED: Added time slot filter dropdown (Horaire) using BaseFilterDropdown
// FIXED: todayStr computed on each render (local date) instead of stale module-level constant

import { RefreshCw, BookOpen, Clock } from 'lucide-react';
import { DateRangePicker } from '../ui/DateRangePicker';
import { SiteSelector } from '../manager/SiteSelector';
import { BaseFilterDropdown } from '../ui/BaseFilterDropdown';
import type { Site } from '../../lib/supabase';
import type { MissionOption } from '../../lib/analytics';

interface AnalyticsFiltersProps {
  sites: Site[];
  selectedSiteId: string | null;
  onSiteChange: (siteId: string) => void;
  dateFrom: string;
  dateTo: string;
  onDateRangeChange: (from: string, to: string) => void;
  missions: MissionOption[];
  selectedMissionId: string | null;
  onMissionChange: (id: string | null) => void;
  availableTimes: string[];
  selectedTime: string | null;
  onTimeChange: (time: string | null) => void;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function getLocalTodayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTimeLabel(time: string): string {
  return time.replace(':', 'h');
}

export function AnalyticsFiltersBar({
  sites,
  selectedSiteId,
  onSiteChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  missions,
  selectedMissionId,
  onMissionChange,
  availableTimes,
  selectedTime,
  onTimeChange,
  isRefreshing,
  onRefresh
}: AnalyticsFiltersProps) {
  const todayStr = getLocalTodayStr();

  const handleMissionSelectionChange = (ids: string[]) => {
    onMissionChange(ids.length > 0 ? ids[0] : null);
  };

  const handleTimeSelectionChange = (times: string[]) => {
    onTimeChange(times.length > 0 ? times[0] : null);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onDateRangeChange}
          maxDate={todayStr}
        />

        {sites.length > 0 && (
          <SiteSelector
            sites={sites}
            selectedSiteId={selectedSiteId}
            onSiteChange={onSiteChange}
            compact
          />
        )}

        {missions.length > 0 && (
          <BaseFilterDropdown
            items={missions}
            selectedItems={selectedMissionId ? [selectedMissionId] : []}
            onSelectionChange={handleMissionSelectionChange}
            getItemId={(m) => m.templateId}
            getItemLabel={(m) => m.templateName}
            label="Cours"
            icon={<BookOpen size={16} />}
            placeholder="Sélectionner un cours"
            emptyMessage="Aucun cours disponible"
            singleSelection={true}
            compact={true}
            searchable={false}
            showSelectAll={false}
            showFooter={false}
          />
        )}

        {availableTimes.length > 0 && (
          <BaseFilterDropdown
            items={availableTimes}
            selectedItems={selectedTime ? [selectedTime] : []}
            onSelectionChange={handleTimeSelectionChange}
            getItemId={(t) => t}
            getItemLabel={(t) => formatTimeLabel(t)}
            label={selectedTime ? formatTimeLabel(selectedTime) : 'Horaire'}
            icon={<Clock size={16} />}
            placeholder="Tous les horaires"
            emptyMessage="Aucun horaire disponible"
            singleSelection={true}
            compact={true}
            searchable={false}
            showSelectAll={false}
            showFooter={false}
          />
        )}
      </div>

      <button
        type="button"
        onClick={onRefresh}
        disabled={isRefreshing}
        className="flex items-center justify-center p-2 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        title="Actualiser"
      >
        <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
      </button>
    </div>
  );
}
